import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ArrowLeft, Loader2 } from 'lucide-react';

const Logo = () => (
  <svg viewBox="0 0 48 48" className="w-10 h-10">
    <rect x="8" y="11" width="32" height="6" rx="2" fill="#111827" />
    <rect x="8" y="21" width="22" height="6" rx="2" fill="#374151" />
    <rect x="8" y="31" width="12" height="6" rx="2" fill="#FCD34D" />
  </svg>
);

export default function AuthPage({ onNavigate, initialMode = 'signup' }) {
  const { login, signup, formatApiErrorDetail } = useAuth();
  const [mode, setMode] = useState(initialMode); // 'login' | 'signup'
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Form fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [name, setName] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setError('');
    setBusy(true);
    try {
      if (mode === 'signup') {
        await signup({ company_name: companyName, email, password, name });
      } else {
        await login(email, password);
      }
      onNavigate('dashboard');
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex">
      {/* Left: form */}
      <div className="flex-1 flex flex-col px-6 py-8 md:px-16">
        <button
          data-testid="auth-back-btn"
          onClick={() => onNavigate('landing')}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 mb-12 self-start"
        >
          <ArrowLeft className="w-4 h-4" /> Back to home
        </button>

        <div className="max-w-md w-full mx-auto flex-1 flex flex-col justify-center">
          <div className="flex items-center gap-3 mb-10">
            <Logo />
            <div>
              <div className="font-semibold text-gray-900">Simplified Helpdesk</div>
              <div className="text-xs text-gray-500">Automation-first IT support</div>
            </div>
          </div>

          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="auth-title">
            {mode === 'signup' ? 'Start your account' : 'Welcome back'}
          </h1>
          <p className="text-gray-600 mb-8">
            {mode === 'signup'
              ? 'Create your company workspace in under a minute. No credit card required.'
              : 'Sign in to your Simplified Helpdesk dashboard.'}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-form">
            {mode === 'signup' && (
              <>
                <div>
                  <Label htmlFor="company">Company name</Label>
                  <Input
                    id="company"
                    data-testid="signup-company-input"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="Acme Corp"
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="name">Your name</Label>
                  <Input
                    id="name"
                    data-testid="signup-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Jane Doe"
                    required
                    className="mt-1"
                  />
                </div>
              </>
            )}

            <div>
              <Label htmlFor="email">Work email</Label>
              <Input
                id="email"
                data-testid="auth-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                className="mt-1"
                autoComplete="email"
              />
            </div>

            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                data-testid="auth-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={mode === 'signup' ? 'At least 8 characters' : '••••••••'}
                required
                minLength={mode === 'signup' ? 8 : undefined}
                className="mt-1"
                autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              />
            </div>

            {error && (
              <div
                data-testid="auth-error"
                className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md px-3 py-2"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              data-testid="auth-submit-btn"
              disabled={busy}
              className="w-full bg-gray-900 hover:bg-gray-800 text-white py-6 text-base"
            >
              {busy ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : mode === 'signup' ? (
                'Create my workspace'
              ) : (
                'Sign in'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-gray-500">
            {mode === 'signup' ? (
              <>
                Already have an account?{' '}
                <button
                  data-testid="switch-to-login"
                  onClick={() => {
                    setMode('login');
                    setError('');
                  }}
                  className="text-gray-900 underline font-medium"
                >
                  Sign in
                </button>
              </>
            ) : (
              <>
                New to Simplified Helpdesk?{' '}
                <button
                  data-testid="switch-to-signup"
                  onClick={() => {
                    setMode('signup');
                    setError('');
                  }}
                  className="text-gray-900 underline font-medium"
                >
                  Start free
                </button>
              </>
            )}
          </div>

          <p className="text-xs text-gray-400 mt-10 text-center">
            By continuing you agree to our{' '}
            <a href="/terms.html" className="underline">Terms</a> and{' '}
            <a href="/privacy.html" className="underline">Privacy Policy</a>.
          </p>
        </div>
      </div>

      {/* Right: brand panel */}
      <div className="hidden lg:flex flex-1 bg-gray-900 text-white p-16 flex-col justify-between">
        <div></div>
        <div className="max-w-lg">
          <h2 className="text-4xl font-bold leading-tight mb-6">
            IT support without
            <br />
            the hassle.
          </h2>
          <p className="text-gray-300 text-lg leading-relaxed mb-8">
            Autonomous helpdesk that answers tickets instantly, resolves common issues
            automatically, and reduces human workload by up to 80%.
          </p>
          <ul className="space-y-3 text-sm text-gray-300">
            <li>✓ No contracts. Cancel anytime.</li>
            <li>✓ Price locked for life.</li>
            <li>✓ Pay once with Lifetime — never billed again.</li>
            <li>✓ 5-minute setup. Integrates with your existing systems.</li>
          </ul>
        </div>
        <div className="text-xs text-gray-500">© 2026 Simplified Helpdesk</div>
      </div>
    </div>
  );
}
